import { getLabelFromStorage, consoleEvents, fetchJwt, setJwt } from '@pb/utils'
import { batch } from 'redux-act'

import { CUSTOMER_TYPES } from 'App/constants'
import appActions, { setMiddlePageEmail } from 'App/duck/actions'
import appSelectors from 'App/duck/selectors'
import OPTIMIZELY from 'constants/optimizely'
import {
  CHECKOUT_TRACKING_ERROR_TYPES,
  CHECKOUT_TRACKING_SECTIONS,
  SHOW_NOTIFICATION_TIME,
  TECHNICAL_ERROR_LABELS,
} from 'features/checkout/constants'
import {
  updateCustomerDetailsFail,
  updateCustomerDetailsStart,
  updateCustomerDetailsSuccess,
} from 'features/checkout/duck/actions'
import {
  normalizeContextPartial,
  normalizeCustomerDetails,
  normalizeUser,
} from 'features/checkout/duck/normalize'
import checkoutSelectors from 'features/checkout/duck/selectors'
import { postCustomerDetails, putCustomerDetails } from 'features/checkout/requests'
import { showNotification } from 'features/notifications/duck/actions'
import { setOrderTotals } from 'features/orderTotals/duck/actions'
import getErrorData from 'helpers/getErrorData'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import { ThunkResult } from 'redux/types'
import { ApiError } from 'types'

import { trackError } from './track'

export const operationUpdateCustomerDetails =
  (formData: any): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(updateCustomerDetailsStart())
    try {
      const customerType = appSelectors.getCustomerType(getState()) || null
      const customerDetails = normalizeCustomerDetails(formData)
      const isReturningGuest = checkoutSelectors.getIsReturningGuest(getState())
      const isRegisteredCustomer = customerType === CUSTOMER_TYPES.REGISTERED
      const shouldUpdateCustomerData = isRegisteredCustomer || isReturningGuest
      const customerDetailsResponse = shouldUpdateCustomerData
        ? await putCustomerDetails(customerDetails)
        : await postCustomerDetails(customerDetails)

      const isCiamEnabled = appSelectors.getIsCiamEnabled(getState())
      if (isCiamEnabled && !shouldUpdateCustomerData) {
        const jwt = await fetchJwt()
        if (jwt?.accessToken) {
          setJwt(jwt)
        }
      }
      const normalizedUser = normalizeUser(customerDetailsResponse.customer)
      dispatch(appActions.updateUser(normalizedUser))

      const normalizedContext = dispatch(normalizeContextPartial(customerDetailsResponse))
      dispatch(updateCustomerDetailsSuccess(normalizedContext))

      // set order totals if it is their first time posting their customer details
      if (!shouldUpdateCustomerData && customerDetailsResponse.orderTotals) {
        dispatch(setOrderTotals(customerDetailsResponse.orderTotals))
      }
      dispatch(setMiddlePageEmail(''))
      sessionStorage.removeItem('improvedLoginFlow')
      trackOptimizelyEvent(OPTIMIZELY.SET_CUSTOMER_TYPE_EVENT, undefined, {
        customerType: normalizedUser.customerType,
      })
    } catch (error) {
      consoleEvents.error('Failed to update customer details', error)
      const { isClientError, isTechnicalError, code, status } = getErrorData(error)
      if (isTechnicalError || status === 405) {
        const errorMessageKey =
          status === 405 ? TECHNICAL_ERROR_LABELS.generalNoAction : TECHNICAL_ERROR_LABELS.general
        const errorMessage = getLabelFromStorage(errorMessageKey)
        dispatch(
          batch(
            showNotification({
              content: errorMessage,
              type: 'error',
              timespan: SHOW_NOTIFICATION_TIME,
            }),
            updateCustomerDetailsFail([]),
          ),
        )
        dispatch(
          trackError({
            section: CHECKOUT_TRACKING_SECTIONS.myInformation,
            message: errorMessage,
            id: code,
            errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
          }),
        )
        return
      }
      if (isClientError) {
        const translateMessage = (message: string): string => {
          if (message === 'customer.phone.invalid') {
            const phoneFormat = appSelectors.getPhoneNumberFormat(getState())
            return getLabelFromStorage(message, { replacements: [phoneFormat || null] })
          }
          return getLabelFromStorage(message)
        }

        const normalizedErrors = error.response.data.map((fieldError: ApiError) => ({
          ...fieldError,
          message: fieldError.message && translateMessage(fieldError.message),
          // These replacements are needed because of difference in FE form schema and BE response
          target: fieldError.target?.replace('address.', '').replace('birthdate', 'dateOfBirth'),
        }))

        dispatch(updateCustomerDetailsFail(normalizedErrors))
      }
    }
  }
